import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useQuery } from '@tanstack/react-query';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import { getFileStreamUrl } from '@eliadmin/core/src/api/video/video-api';
import { ResponsiveDialogProvider } from '@eliadmin/core/src/components/ui/responsive-dialog';
import VideoPresentationPaymentDialog from './video-payment-dialog';
import { VideoGuideDialog } from './video-guide-dialog';
import { fetchPaymentTypes } from '@eliadmin/core/src/api/jobportal/offers-api';

export const ProfileAddVideo = ({
  type,
  customTitle = 'Pridať video',
  customDescription = 'Pridajte video k svojmu profilu',
  disabled = false,
}: {
  type: 'company' | 'student' | 'agency' | 'all_perms';
  customTitle?: string;
  customDescription?: string;
  disabled?: boolean;
}) => {
  const { control, watch, setValue } = useFormContext();
  const allPerms = type === 'all_perms';
  const videoFile = watch('video');
  const authToken = useCustomerStore(s => s.me?.authToken) || '';
  const videoThatWasUploaded = watch('video_current');
  const me = useCustomerStore(s => s.me);
  const isStudent = me?.customer?.customer_type && me?.customer?.customer_type.type === 'student';
  const hasVideoSubscription = true;

  const locale = useLocaleStore(s => s.currentLocale);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGuideDialogOpen, setIsGuideDialogOpen] = useState(false);

  // load payment methods
  const { data: payments } = useQuery({
    queryKey: ['cart-payments', locale, authToken || null],
    queryFn: () => fetchPaymentTypes({ locale, authToken }).then(r => r.data),
    enabled: !!authToken,
  });

  // load video presentation product
  const { data: videoPresentationProduct } = useQuery({
    queryKey: ['video-presentation-product', locale, 15, authToken || null],
    queryFn: async () => {
      // adjust filter as needed
      const slugRes = await fetchProducts({
        locale,
        authToken,
        query: { where: { id: { in: [15] } } },
      }).then(r => r.data[0]?.slug);
      if (!slugRes) return;
      const detail = await fetchSlugDetail({
        locale,
        slug: slugRes,
        authToken,
        cache: 'no-store',
      });
      return detail['App\\Models\\Product'];
    },
    enabled: !!authToken,
  });

  // 1️⃣ Local preview URL for newly uploaded file
  const localPreviewUrl = useMemo(
    () => (videoFile ? URL.createObjectURL(videoFile) : null),
    [videoFile],
  );

  const videoRef = useRef<HTMLVideoElement>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewSource, setPreviewSource] = useState<string | null>(null);

  // Show new-upload immediately
  useEffect(() => {
    if (localPreviewUrl) {
      setPreviewSource(localPreviewUrl);
      setShowPreview(true);
    }
  }, [localPreviewUrl]);

  // Show existing upload
  const handleShowExisting = () => {
    if (!videoThatWasUploaded?.id) return;
    const rawUrl = getFileStreamUrl(videoThatWasUploaded.id);
    setPreviewSource(rawUrl);
    setShowPreview(true);
  };

  if (type === 'company' || type === 'agency') {
    return null;
  }

  return (
    <ResponsiveDialogProvider>
      <div className="relative">
        {/* overlay, only when disabled */}
        {disabled && (
          <div
            className="
            absolute inset-0 
            z-10 cursor-not-allowed 
            bg-white bg-opacity-50 
            backdrop-grayscale 
            backdrop-filter
          "
            // make sure it catches all clicks
            style={{ pointerEvents: 'auto' }}
          />
        )}
        <div className="flex w-full flex-col gap-4 md:gap-[28px]">
          <h1 className="text-2xl font-bold md:text-3xl">{customTitle}</h1>
          <p>{customDescription}</p>

          {/* Show purchase dialog if no subscription */}
          {!hasVideoSubscription && (
            <Button type="button" onClick={() => setIsDialogOpen(true)}>
              Zakúpiť
            </Button>
          )}

          {/* Upload and preview if subscribed */}
          {hasVideoSubscription && (
            <>
              <Controller
                name="video"
                control={control}
                defaultValue={null}
                render={({ field }) => (
                  <label className="inline-block cursor-pointer rounded-lg bg-blue-600 p-3 text-white hover:bg-blue-700">
                    Vybrať video
                    <input
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={e => field.onChange(e.target.files?.[0] ?? null)}
                    />
                  </label>
                )}
              />

              {!videoFile && videoThatWasUploaded && !showPreview && (
                <div className="flex gap-2">
                  <Button onClick={handleShowExisting} type="button">
                    Zobraziť existujúce video
                  </Button>
                  <Button
                    onClick={() => {
                      // Clear the existing video using React Hook Form
                      setValue('video_current', null);
                      setValue('video', null);
                      setValue('company_contact_url', null);
                    }}
                    type="button"
                    variant="destructive"
                  >
                    Odstrániť video
                  </Button>
                </div>
              )}

              {showPreview && previewSource && (
                <div className="mt-4">
                  <video
                    ref={videoRef}
                    src={previewSource}
                    poster="/images/placeholder.png"
                    controls
                    className="w-full rounded-lg border border-gray-300"
                    onError={e => console.error('Video playback error:', e)}
                  />
                  <div className="mt-2 flex gap-2">
                    {videoFile && (
                      <Button
                        onClick={() => {
                          // Clear the new video file using React Hook Form
                          setValue('video', null);
                          setValue('video_current', null);
                          setValue('company_contact_url', null);
                          // Reset preview
                          setShowPreview(false);
                          setPreviewSource(null);
                          if (localPreviewUrl) {
                            URL.revokeObjectURL(localPreviewUrl);
                          }
                        }}
                        type="button"
                        variant="destructive"
                      >
                        Odstrániť nové video
                      </Button>
                    )}
                    {videoThatWasUploaded && !videoFile && (
                      <Button
                        onClick={() => {
                          // Clear the existing video using React Hook Form
                          setValue('video', null);
                          setValue('video_current', null);
                          setValue('company_contact_url', null);
                          // Reset preview
                          setShowPreview(false);
                          setPreviewSource(null);
                        }}
                        type="button"
                        variant="destructive"
                      >
                        Odstrániť existujúce video
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Payment Dialog */}
        <VideoPresentationPaymentDialog
          isOpen={isDialogOpen}
          setIsOpen={setIsDialogOpen}
          videoPresentationProduct={videoPresentationProduct}
          payments={payments}
          type={type}
        />

        {/* Guide Dialog */}
        <VideoGuideDialog isOpen={isGuideDialogOpen} setIsOpen={setIsGuideDialogOpen} />
      </div>
      {/* Guide Button */}
      {isStudent && (
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsGuideDialogOpen(true)}
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            Návod ako vytvoriť videoživotopis
          </Button>
        </div>
      )}
    </ResponsiveDialogProvider>
  );
};
